import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Grid,
  Card,
  CardMedia,
  CardContent,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Image } from '../types';
import ImageService from '../services/imageService';
import InventoryService from '../services/inventoryService';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/apiConfig';
import { fetchWithAuth } from '../utils/authSession';
import { chaaviGoldAddImageButtonSx } from './ChaaviVendorDashboardShell';

interface ImageUploadProps {
  open?: boolean;
  onClose?: () => void;
  themeId: string; // This is used for both theme and inventory IDs
  themeName?: string;
  onImagesChange?: (images: Image[]) => void;
  onUploadSuccess?: () => void;
  uploadType?: 'theme' | 'inventory';
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  open = true,
  onClose,
  themeId,
  themeName = 'Item',
  onImagesChange,
  onUploadSuccess,
  uploadType = 'theme',
}) => {
  const { user } = useAuth();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const validFiles = files.filter(file => {
      const isValidType = file.type.startsWith('image/');
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB limit (base64 increases size by ~33%)
      return isValidType && isValidSize;
    });

    if (validFiles.length !== files.length) {
      setError('Some files were rejected. Only image files under 5MB are allowed.');
    } else {
      setError(null);
    }

    setSelectedFiles(validFiles);
    
    // Create preview URLs
    const urls = validFiles.map(file => URL.createObjectURL(file));
    setPreviewUrls(urls);
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    const newUrls = previewUrls.filter((_, i) => i !== index);
    
    // Revoke the URL to free memory
    URL.revokeObjectURL(previewUrls[index]);
    
    setSelectedFiles(newFiles);
    setPreviewUrls(newUrls);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one image to upload.');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const uploadPromises = selectedFiles.map(async (file, index) => {
        // Create FormData for file upload
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', uploadType === 'inventory' ? 'inventory' : 'themes');
        formData.append('itemId', themeId);

        // Get authentication token
        const token = localStorage.getItem('token');
        const headers: HeadersInit = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        // Upload file to backend
        const uploadResponse = await fetchWithAuth(`${API_BASE_URL}/api/files/upload`, {
          method: 'POST',
          headers: headers,
          body: formData,
        });

        let uploadResult;
        try {
          uploadResult = await uploadResponse.json();
        } catch (parseError) {
          // If JSON parsing fails, throw a generic error
          throw new Error(`File upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
        }

        if (!uploadResponse.ok || !uploadResult.success) {
          const errorMessage = uploadResult.message || `File upload failed: ${uploadResponse.status}`;
          console.error('Upload error response:', uploadResult);
          throw new Error(errorMessage);
        }
        
        if (uploadType === 'inventory') {
          const imageData = {
            inventoryId: themeId,
            imageName: file.name,
            imageUrl: `${API_BASE_URL}${uploadResult.filePath}`,
            imagePath: uploadResult.filePath,
            imageSize: file.size,
            imageType: file.type,
          };

          console.log('Preparing to save inventory image:', imageData);
          return await InventoryService.createInventoryImage(imageData, user?.phoneNumber);
        } else {
          const imageData = {
            themeId,
            imageName: file.name,
            imageUrl: `${API_BASE_URL}${uploadResult.filePath}`,
            imagePath: uploadResult.filePath,
            imageSize: file.size,
            imageType: file.type,
          };

          return await ImageService.createImage(imageData, user?.phoneNumber);
        }
      });

      await Promise.all(uploadPromises);
      
      // Get updated images list
      if (uploadType === 'inventory') {
        // For inventory images, we don't need to update the parent's image list
        // The parent component will handle refreshing
      } else {
        const updatedImages = await ImageService.getImagesByThemeId(themeId);
        if (onImagesChange) {
          onImagesChange(updatedImages);
        }
      }
      
      // Reset form
      setSelectedFiles([]);
      setPreviewUrls([]);
      previewUrls.forEach(url => URL.revokeObjectURL(url));
      
      // Call success callback first
      if (onUploadSuccess) {
        onUploadSuccess();
      }
      
      // Close dialog if onClose is provided
      if (onClose) {
        onClose();
      }
    } catch (err: any) {
      console.error('Upload error:', err);
      // err.message should now contain the actual error message from backend
      const errorMessage = err.message || 'File upload failed. Please try again.';
      setError(`Upload failed: ${errorMessage}`);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    // Clean up preview URLs
    previewUrls.forEach(url => URL.revokeObjectURL(url));
    setSelectedFiles([]);
    setPreviewUrls([]);
    setError(null);
    if (onClose) {
      onClose();
    }
  };

  // If onClose is not provided, render as a simple form (not a dialog)
  if (!onClose) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Upload Images for "{themeName}"
        </Typography>
        
        <Box>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            
            <Button
              variant="contained"
              startIcon={<UploadIcon sx={{ color: '#fff' }} />}
              onClick={() => fileInputRef.current?.click()}
              fullWidth
              sx={{ mb: 2, ...chaaviGoldAddImageButtonSx }}
            >
              Select Images
            </Button>
            
            <Typography variant="body2" color="text.secondary" align="center">
              Select multiple images (JPG, PNG, GIF). Max 5MB per file.
            </Typography>
          </Box>

          {selectedFiles.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Selected Images ({selectedFiles.length})
              </Typography>
              
              <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ padding: { xs: 1, sm: 2 } }}>
                {selectedFiles.map((file, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                    <Card sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 'clamp(8px, 1vw, 12px)',
                      overflow: 'hidden',
                      boxShadow: 2,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                      }
                    }}>
                      <CardMedia
                        component="img"
                        height="240"
                        image={previewUrls[index]}
                        alt={file.name}
                        sx={{ 
                          objectFit: 'cover',
                          width: '100%',
                          height: '240px',
                          flexGrow: 1
                        }}
                      />
                      <CardContent sx={{ 
                        padding: { xs: 'clamp(8px, 1.5vw, 12px)', sm: 'clamp(12px, 2vw, 16px)' },
                        flexGrow: 0
                      }}>
                        <Typography variant="body2" noWrap sx={{ mb: 0.5, fontWeight: 500 }}>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                        <Box sx={{ 
                          mt: 1,
                          pt: 1,
                          borderTop: '1px solid',
                          borderColor: 'divider'
                        }}>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveFile(index)}
                            color="error"
                            sx={{ padding: { xs: 'clamp(4px, 1vw, 8px)', sm: 1 } }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {selectedFiles.length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Button
                onClick={handleUpload}
                variant="contained"
                disabled={selectedFiles.length === 0 || uploading}
                startIcon={
                  uploading ? (
                    <CircularProgress size={20} sx={{ color: '#fff' }} />
                  ) : (
                    <UploadIcon sx={{ color: '#fff' }} />
                  )
                }
                size="large"
                sx={chaaviGoldAddImageButtonSx}
              >
                {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} Image${selectedFiles.length !== 1 ? 's' : ''}`}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // Dialog version (when onClose is provided)
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Upload Images for "{themeName}"
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          
          <Button
            variant="contained"
            startIcon={<UploadIcon sx={{ color: '#fff' }} />}
            onClick={() => fileInputRef.current?.click()}
            fullWidth
            sx={{ mb: 2, ...chaaviGoldAddImageButtonSx }}
          >
            Select Images
          </Button>
          
          <Typography variant="body2" color="text.secondary" align="center">
            Select multiple images (JPG, PNG, GIF). Max 5MB per file.
          </Typography>
        </Box>

        {selectedFiles.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Selected Images ({selectedFiles.length})
            </Typography>
            
            <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ padding: { xs: 1, sm: 2 } }}>
              {selectedFiles.map((file, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                  <Card sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 'clamp(8px, 1vw, 12px)',
                    overflow: 'hidden',
                    boxShadow: 2,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    }
                  }}>
                    <CardMedia
                      component="img"
                      height="240"
                      image={previewUrls[index]}
                      alt={file.name}
                      sx={{ 
                        objectFit: 'cover',
                        width: '100%',
                        height: '240px',
                        flexGrow: 1
                      }}
                    />
                    <CardContent sx={{ 
                      padding: { xs: 'clamp(8px, 1.5vw, 12px)', sm: 'clamp(12px, 2vw, 16px)' },
                      flexGrow: 0
                    }}>
                      <Typography variant="body2" noWrap sx={{ mb: 0.5, fontWeight: 500 }}>
                        {file.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                      <Box sx={{ 
                        mt: 1,
                        pt: 1,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveFile(index)}
                          color="error"
                          sx={{ padding: { xs: 'clamp(4px, 1vw, 8px)', sm: 1 } }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={selectedFiles.length === 0 || uploading}
          startIcon={
            uploading ? (
              <CircularProgress size={20} sx={{ color: '#fff' }} />
            ) : (
              <UploadIcon sx={{ color: '#fff' }} />
            )
          }
          sx={chaaviGoldAddImageButtonSx}
        >
          {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} Image${selectedFiles.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageUpload;
